import DeleteIcon from '@material-ui/icons/Delete';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { CloudDownload } from '@material-ui/icons';
import { AppContext } from 'context';
import ActionButton from 'components/Buttons/ActionButton';
import EditIcon from 'components/Icons/EditIcon';
import { TableFilterContext } from 'tableFilterContext';
import { useCharacterizationsWithFilter } from 'hooks';
import { checkDateUpperTimeInterval, createUTCDateFromDateString } from 'utils/date';
import { useLatestClosureDate } from 'utils/closureHooks';
import { Routes } from 'routes';
import { EntityColumn, EntityColumnType } from 'components/List/TableCellWithConfigurableStyle';
import GenericList from 'components/List/GenericList';
import DeleteConfirmationDialog from 'components/Generic/DeleteConfirmationDialog';
import { deleteCharacterization, downloadCharacterizationDetailReport } from 'services/apiOperations';
import { notify } from 'components/Notifier';
import { CharacterizationFilters, UserRole } from 'types';
import { generateNotifyFromViolations, isSubmissionErrorType } from 'services/errors';
import { CharacterizationFilterBar } from './CharacterizationFilterBar';

const columns: EntityColumn[] = [
  {
    columnPath: '',
    headerTranslationKey: 'characterizations.action',
    type: 'action' as EntityColumnType,
    disableSort: true,
    style: {
      columnWidth: '5%',
    },
  },
  {
    columnPath: 'samplingDate',
    headerTranslationKey: 'characterizations.samplingDate',
    type: 'date' as EntityColumnType,
    style: {
      columnWidth: '25%',
      paddingLeft: '3rem',
    },
  },
  {
    columnPath: 'client.shortName',
    headerTranslationKey: 'characterizations.client',
    style: {
      columnWidth: '25%',
    },
  },
  {
    columnPath: 'stream.name',
    headerTranslationKey: 'characterizations.stream',
    style: {
      columnWidth: '20%',
    },
  },
  {
    columnPath: 'weight',
    headerTranslationKey: 'characterizations.weight',
    type: 'weightKg' as EntityColumnType,
    style: {
      columnWidth: '30%',
      paddingRight: '5rem',
    },
  },
];

type CharacterizationListProps = {
  filter: CharacterizationFilters | null;
  setFilter: (filter: CharacterizationFilters | null) => void;
  setExportDisabled: (isExportDisabled: boolean) => void;
};

const CharacterizationList: React.FC<CharacterizationListProps> = ({ filter, setFilter, setExportDisabled }) => {
  const { page, itemsPerPage, order, orderBy } = useContext(TableFilterContext);
  const location = useLocation();
  const history = useHistory();

  const context = useContext(AppContext);
  const roles = context.getRoles?.();
  const sortingCenter = context.getSortingCenter?.();
  const [closureMonth] = useLatestClosureDate(sortingCenter);

  const { t } = useTranslation();
  const [characterizationList, total] = useCharacterizationsWithFilter(
    { page, itemsPerPage },
    { order, orderBy },
    { ...filter, hasMultipleCharacterizations: true },
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const readOnly = !sortingCenter?.isActive || (roles && roles.includes(UserRole.ROLE_CONSULTATION));

  useEffect(() => {
    if (!characterizationList || characterizationList.length === 0) {
      setExportDisabled(true);
      return;
    }
    setExportDisabled(false);
  }, [setExportDisabled, characterizationList]);

  return (
    <>
      <DeleteConfirmationDialog
        title={t('characterizationForm.delete.confirm')}
        isOpen={Boolean(confirmDeleteId)}
        setOpen={() => setConfirmDeleteId(null)}
        confirmDeletion={async () => {
          if (!confirmDeleteId) return;
          setConfirmDeleteId(null);
          try {
            await deleteCharacterization(confirmDeleteId.toString());
            notify({
              message: t('characterizationForm.delete.success'),
              severity: 'success',
            });
          } catch (e: any) {
            if (isSubmissionErrorType(e) && e.status === 412 && e.response.body.violations) {
              generateNotifyFromViolations(e.response.body.violations, t);
            } else {
              notify({
                message: t('errorGeneric'),
                severity: 'error',
              });
            }
          }
        }}
      />
      <div style={{ marginTop: 30 }}>
        <CharacterizationFilterBar
          onChange={setFilter}
          minStartDate={new Date(0)}
          maxEndDate={new Date()}
          defaultEndDate={new Date()}
        />
      </div>
      <GenericList
        entities={characterizationList}
        columns={columns}
        total={total}
        detailsLink={Routes.Characterizations}
        generateAvailableActions={({ id, samplingDate, isTheoreticalComposition, hasMultipleCharacterizations }) => {
          const samplingDatetime = createUTCDateFromDateString(samplingDate);
          const disabled = readOnly || checkDateUpperTimeInterval(samplingDatetime, closureMonth);
          const disabledDelete = disabled || (isTheoreticalComposition && hasMultipleCharacterizations);
          const linkTo = { pathname: `/characterizations/${id}/edit`, state: { referer: location.pathname } };

          return [
            {
              title: t('update'),
              onClick: () => history.push(linkTo.pathname, linkTo.state),
              disabled,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={EditIcon}
                  component={Link}
                  data-qa-id={'edit-button'}
                  to={linkTo}
                  disabled={disabled}
                />
              ),
            },
            {
              title: t('delete'),
              onClick: () => setConfirmDeleteId(id),
              disabled: disabledDelete,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={DeleteIcon}
                  data-qa-id={'delete-button'}
                  onClick={() => setConfirmDeleteId(id)}
                  disabled={disabled}
                />
              ),
            },
            {
              title: t('characterizationForm.show.action.downloadReport'),
              onClick: () => downloadCharacterizationDetailReport(id),
              disabled: false,
              action: (
                <ActionButton
                  variant="outlined"
                  icon={CloudDownload}
                  data-qa-id={'download-button'}
                  onClick={() => downloadCharacterizationDetailReport(id)}
                />
              ),
            },
          ];
        }}
      />
    </>
  );
};
export default CharacterizationList;
